
import { defineComponent } from "vue";
import dayjs from "dayjs";

const MIN_DATE = dayjs("2026-01-01").startOf("day");

const PRESET_OPTIONS = [
  "Today",
  "Last 7 Days",
  "This Month",
  "Last Month",
  "Custom"
] as const;

type PresetOption = (typeof PRESET_OPTIONS)[number];

export default defineComponent({
  name: "SeqDateRangePicker",
  emits: ["change"],
  data() {
    return {
      presetOptions: PRESET_OPTIONS,
      preset: "This Month" as PresetOption,
      presetMenuOpen: false,
      fromDate: "",
      toDate: "",
      customRange: null as string[] | null
    };
  },
  computed: {
    dateRangeLabel(): string {
      if (!this.fromDate || !this.toDate) {
        return "";
      }
      const start = dayjs(this.fromDate).format("DD MMM YYYY");
      const end = dayjs(this.toDate).format("DD MMM YYYY");
      return `${start} - ${end}`;
    }
  },
  mounted() {
    this.applyPreset("This Month");
    document.addEventListener("click", this.handleDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick);
  },
  methods: {
    disabledDate(time: Date) {
      return dayjs(time).isBefore(MIN_DATE, "day");
    },
    clampStartDate(date: dayjs.Dayjs) {
      return date.isBefore(MIN_DATE, "day") ? MIN_DATE : date;
    },
    togglePresetMenu() {
      this.presetMenuOpen = !this.presetMenuOpen;
    },
    handleDocumentClick(event: MouseEvent) {
      const presetRef = this.$refs.presetRef as HTMLElement | undefined;
      if (presetRef && !presetRef.contains(event.target as Node)) {
        this.presetMenuOpen = false;
      }
    },
    selectPreset(option: PresetOption) {
      this.presetMenuOpen = false;
      if (option === "Custom") {
        this.preset = option;
        return;
      }
      this.applyPreset(option);
    },
    applyPreset(option: PresetOption) {
      this.preset = option;
      const today = dayjs().startOf("day");
      let start = today;
      let end = today;

      if (option === "Last 7 Days") {
        start = today.subtract(6, "day");
      } else if (option === "This Month") {
        start = today.startOf("month");
      } else if (option === "Last Month") {
        start = today.subtract(1, "month").startOf("month");
        end = today.subtract(1, "month").endOf("month");
      }

      start = this.clampStartDate(start);
      if (end.isBefore(start, "day")) {
        end = start;
      }

      this.fromDate = start.format("YYYY-MM-DD");
      this.toDate = end.format("YYYY-MM-DD");
      this.customRange = [this.fromDate, this.toDate];
      this.emitChange();
    },
    onCustomRangeChange(value: string[] | null) {
      if (!Array.isArray(value) || value.length !== 2) {
        return;
      }

      let start = dayjs(value[0]);
      let end = dayjs(value[1]);
      start = this.clampStartDate(start);

      if (end.isBefore(start, "day")) {
        end = start;
      }

      this.fromDate = start.format("YYYY-MM-DD");
      this.toDate = end.format("YYYY-MM-DD");
      this.customRange = [this.fromDate, this.toDate];
      this.preset = "Custom";
      this.emitChange();
    },
    emitChange() {
      this.$emit("change", {
        preset: this.preset,
        fromDate: this.fromDate,
        toDate: this.toDate
      });
    }
  }
});
